import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import { type CartLineInput } from '@data/shopify/storefront/types'
import { getTaxCartAttributes, parseShopifyCart } from '@lib/shopify/cart'
import {
  addLineItemsToShopifyCart,
  removeLineItemsFromShopifyCart,
  updateLineItemsInShopifyCart,
  updateShopifyCartAttrbites,
  updateShopifyCartNote,
} from '@lib/shopify/graphql/cart'
import { getShopifyGlobalId } from '@lib/shopify/helpers'
import { type Locale } from '@lib/language'
import { LanguageContext } from '@lib/language-context'
import { ShopContext } from '@lib/shop-context'
import { StringsContext } from '@lib/strings-context'
import { CartContext } from './context'
import { validateCart } from './helpers'
import {
  type Cart,
  type CartFormValues,
  type CartTotals,
  type CartUpdateResult,
  type CartVariantLineItem,
} from './types'

/**
 * Returns cart item count.
 */
export const useCartItemCount = () => {
  const { cart } = useContext(CartContext)

  return useMemo(
    () =>
      cart?.lineItems?.reduce((total, { quantity }) => total + quantity, 0) ??
      0,
    [cart?.lineItems]
  )
}

/**
 * Returns cart totals.
 */
export const useCartTotals = (): CartTotals => {
  const { cart } = useContext(CartContext)

  return useMemo(
    () => ({
      subTotal: cart?.subTotal ?? 0,
      totalDiscount: 0,
      total: cart?.total ?? 0,
    }),
    [cart?.subTotal, cart?.total]
  )
}

export const useAddItemsToCart = (
  cart: Cart,
  locale: Locale,
  setIsCartProductAdding: Dispatch<SetStateAction<boolean>>,
  setIsCartUpdating: Dispatch<SetStateAction<boolean>>,
  saveCart: (locale: Locale, cart?: Cart) => void,
  toggleCart: (newState: boolean) => void
) => {
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  return useCallback(
    async (variantLineItems: CartVariantLineItem[]): Promise<boolean> => {
      if (!cart.id) {
        return false
      }

      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsCartProductAdding(true)
      setIsCartUpdating(true)

      const lines = variantLineItems.map((variantLineItem) => {
        const merchandiseId = getShopifyGlobalId(
          'ProductVariant',
          variantLineItem.id
        )
        const cartLineInput: CartLineInput = {
          merchandiseId,
          quantity: variantLineItem.quantity,
          attributes: [...(variantLineItem.customAttributes ?? [])],
        }

        return cartLineInput
      })
      const cartResponse = await addLineItemsToShopifyCart(
        locale,
        shopifyStorefrontGraphQlClient,
        cart.id,
        lines
      )

      if (cartResponse.error) {
        setIsCartProductAdding(false)
        setIsCartUpdating(false)
        return false
      }

      const newCart = await parseShopifyCart(locale, cartResponse.cart)

      if (!newCart) {
        setIsCartProductAdding(false)
        setIsCartUpdating(false)
        return false
      }

      saveCart(locale, newCart)

      setIsCartProductAdding(false)
      setIsCartUpdating(false)
      toggleCart(false)

      return !!newCart
    },
    [
      cart.id,
      locale,
      saveCart,
      setIsCartProductAdding,
      setIsCartUpdating,
      shopifyStorefrontGraphQlClient,
      toggleCart,
    ]
  )
}

export const useUpdateCartItem = (
  cart: Cart,
  locale: Locale,
  setIsCartUpdating: Dispatch<SetStateAction<boolean>>,
  saveCart: (locale: Locale, cart?: Cart) => void
) => {
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  return useCallback(
    async (id: string, quantity: number) => {
      if (!cart.id) {
        throw new Error('Cart missing')
      }

      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsCartUpdating(true)

      // Update cart line items
      const cartResponse = await updateLineItemsInShopifyCart(
        locale,
        shopifyStorefrontGraphQlClient,
        cart.id,
        [
          {
            id,
            quantity,
          },
        ]
      )

      if (cartResponse.error || cartResponse.userErrors) {
        setIsCartUpdating(false)

        const errorResult: CartUpdateResult = {
          userErrors: cartResponse.userErrors,
          error: cartResponse.error,
        }

        return errorResult
      }

      const newCart = await parseShopifyCart(locale, cartResponse.cart)

      setIsCartUpdating(false)

      if (!newCart) {
        throw new Error('Cart parsing after update failed')
      }

      saveCart(locale, newCart)

      const successResult: CartUpdateResult = {
        cart: newCart,
      }

      return successResult
    },
    [
      cart.id,
      locale,
      saveCart,
      setIsCartUpdating,
      shopifyStorefrontGraphQlClient,
    ]
  )
}

export const useRemoveItemFromCart = (
  cart: Cart,
  locale: Locale,
  setIsCartUpdating: Dispatch<SetStateAction<boolean>>,
  saveCart: (locale: Locale, cart?: Cart) => void
) => {
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  return useCallback(
    async (id: string): Promise<boolean> => {
      if (!cart.id) {
        return false
      }

      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsCartUpdating(true)

      // Remove line item from Shopify cart
      const cartResponse = await removeLineItemsFromShopifyCart(
        locale,
        shopifyStorefrontGraphQlClient,
        cart.id,
        [id]
      )

      if (cartResponse.error) {
        return false
      }

      const newCart = await parseShopifyCart(locale, cartResponse.cart)

      if (!newCart) {
        return false
      }

      saveCart(locale, newCart)

      setIsCartUpdating(false)

      return !!newCart
    },
    [
      cart.id,
      locale,
      saveCart,
      setIsCartUpdating,
      shopifyStorefrontGraphQlClient,
    ]
  )
}

export const useSubmitCart = (
  cart: Cart,
  setIsCartSubmitting: Dispatch<SetStateAction<boolean>>
) => {
  const { locale } = useContext(LanguageContext)
  const { countryCode, shopifyStorefrontGraphQlClient } =
    useContext(ShopContext)
  const strings = useContext(StringsContext)

  return useCallback(
    async (values: CartFormValues) => {
      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsCartSubmitting(true)

      // Validate cart form
      const { errors, vatIdCountryCode } = await validateCart(strings, values)

      if (cart?.id && Object.entries(errors).length === 0) {
        // Update cart attributes
        const taxCartAttributes = getTaxCartAttributes(
          values,
          vatIdCountryCode !== countryCode
        )

        await updateShopifyCartAttrbites(
          locale,
          shopifyStorefrontGraphQlClient,
          cart.id,
          [...taxCartAttributes]
        )

        // Update cart note
        await updateShopifyCartNote(
          locale,
          shopifyStorefrontGraphQlClient,
          cart.id,
          values.comment ?? ''
        )
      }

      // Wait for potential analytics event to finish
      setTimeout(async () => {
        setIsCartSubmitting(false)
      }, 1000)

      return { errors }
    },
    [
      cart.id,
      countryCode,
      locale,
      setIsCartSubmitting,
      shopifyStorefrontGraphQlClient,
      strings,
    ]
  )
}
